import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function CommentPanel({ workflowId, onClose }) {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const bottomRef = useRef(null);

    useEffect(() => {
        fetchComments();
        const interval = setInterval(fetchComments, 10000); // Poll every 10s
        return () => clearInterval(interval);
    }, [workflowId]);

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [comments]);

    const fetchComments = async () => {
        try {
            const response = await fetch(`${API}/workflows/${workflowId}/comments`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setComments(data);
            }
        } catch (error) {
            console.error('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        try {
            const response = await fetch(`${API}/workflows/${workflowId}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ content: newComment })
            });

            if (response.ok) {
                setNewComment('');
                fetchComments();
            }
        } catch (error) {
            toast.error('Failed to post comment');
        }
    };

    return (
        <div className="fixed right-0 top-0 h-screen w-80 bg-background-paper border-l border-white/5 z-40 flex flex-col shadow-2xl animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5 bg-black/20">
                <h3 className="font-heading font-bold">Comments</h3>
                <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                    <div className="flex justify-center p-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-text-muted">
                        <p>No comments yet</p>
                        <p className="text-xs mt-1">Start the conversation!</p>
                    </div>
                ) : (
                    comments.map((comment) => (
                        <div key={comment.comment_id} className="animate-fade-in">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                                    <User className="w-3 h-3 text-primary" />
                                </div>
                                <span className="text-sm font-medium text-white">{comment.user_name}</span>
                                <span className="text-xs text-text-muted">
                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                </span>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 ml-8 border border-white/5 text-sm text-text-secondary">
                                {comment.content}
                            </div>
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/5 bg-black/20">
                <form onSubmit={handleSubmit} className="flex gap-2">
                    <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Type a comment..."
                        className="glass-input flex-1 text-sm py-2 px-3"
                    />
                    <button
                        type="submit"
                        disabled={!newComment.trim()}
                        className="p-2 bg-primary text-black rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CommentPanel;
